import { useState } from 'react';
import { IosPlaceholder } from 'ui/icons';
import { TemplatePreviewImageEmptyState } from 'ui/illustrations';
import { pxToRem } from 'utils/convert';
import { validateImage } from 'utils/imageUrlValidator';
import { MAX_CHAR_LIMIT } from '../constants';
import { displayContent, truncateText } from '../../common';
import { OS, PushPreviewType, TemplateEditViews } from '../../common/types';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { usePushPreviewContext } from '../context/PushPreviewContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { PushContentDefinition } from '../../../graphql/types';
import { PreviewActionButtons } from '../PreviewActionButtons';
import {
  expandedNotificationAttachmentIos,
  expandedNotificationAttachmentWrapperIos,
  expandedNotificationBubble,
  expandedNotificationBubbleIosContent,
  expandedNotificationBubbleIosContentHeader,
  expandedNotificationBubbleIosContentHeaderSender,
  expandedNotificationBubbleIosContentHeaderTime,
  expandedNotificationBubbleIosHeaderAndBodyWrapper,
  expandedNotificationBubbleWrapper,
  expandedNotificationPlaceholder,
  notificationBubbleIosContentBody,
  notificationBubbleIosContentBodyMsg,
  notificationContentWrapper,
} from './styles';

export const ExpandedNotificationIos = () => {
  const { liveTemplate } = useEditableFieldsContext();
  const { view } = useQueryParamContext();
  const { messageContentBody, messageContentTitle, messageActionButtons, messageAttachmentUrlIos } =
    usePushPreviewContext();
  const { isDataFeedDataEmpty, isUserDataEmpty } = usePreviewWithDataContext();
  const usePopulatedField =
    view === TemplateEditViews.preview && (!isDataFeedDataEmpty || !isUserDataEmpty);
  const pushData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const title = usePopulatedField ? messageContentTitle : pushData.title || '';
  const message = usePopulatedField ? messageContentBody : pushData.message || '';
  const attachmentUrlIos = usePopulatedField
    ? messageAttachmentUrlIos
    : pushData.attachmentUrlIos || '';
  const [isImageUrlValid, setIsImageUrlValid] = useState<boolean>(false);
  const actionButtons = usePopulatedField ? messageActionButtons : pushData.actionButtons || [];

  const urlValidation = async () => {
    const isValidImage = await validateImage(attachmentUrlIos || '');
    // @ts-ignore
    setIsImageUrlValid(isValidImage);
  };

  urlValidation().then(() => undefined);

  return (
    <div data-test="expanded-notification-preview-ios" style={expandedNotificationBubbleWrapper}>
      <div style={expandedNotificationBubble[OS.ios]}>
        <div style={expandedNotificationBubbleIosContent}>
          <div style={expandedNotificationBubbleIosContentHeader}>
            <div style={expandedNotificationPlaceholder}>
              <IosPlaceholder height={pxToRem(24)} width={pxToRem(24)} />
            </div>
            <div style={expandedNotificationBubbleIosHeaderAndBodyWrapper}>
              <div
                style={expandedNotificationBubbleIosContentHeaderSender}
                data-test={'push-preview-ios-title'}
              >
                {title}
              </div>
              <div style={notificationContentWrapper}>
                <div style={notificationBubbleIosContentBody}>
                  {/* SMS, Push, & WebPush do not support HTML, so do not use
                   * dangerouslySetInnerHTML attr to display content (force text rendering)
                   * Email & InApp DO support HTML and should be displayed with
                   * dangerouslySetInnerHTML={{ __html: removeScript(content) }} so that HTML
                   * renders and scripts are removed prior to rendering
                   */}
                  <div
                    style={notificationBubbleIosContentBodyMsg}
                    data-test={'push-preview-ios-message'}
                  >
                    {truncateText(message, MAX_CHAR_LIMIT.ios)}
                  </div>
                </div>
              </div>
            </div>
            <div style={expandedNotificationBubbleIosContentHeaderTime}>
              {displayContent.strNow}
            </div>
          </div>
        </div>
        {pushData.attachmentUrlIos && (
          <div style={expandedNotificationAttachmentWrapperIos}>
            {isImageUrlValid ? (
              <img
                data-test={'push-preview-ios-image'}
                style={expandedNotificationAttachmentIos}
                src={attachmentUrlIos}
                alt="IOS Push Image"
              />
            ) : (
              <TemplatePreviewImageEmptyState data-test={'push-preview-ios-empty-image'} />
            )}
          </div>
        )}
      </div>
      {actionButtons && actionButtons.length > 0 && (
        <PreviewActionButtons
          buttons={actionButtons}
          selectedPreviewType={PushPreviewType.expandedNotification}
        />
      )}
    </div>
  );
};
